import { useMemo } from 'react';

import {
  A11Y_SELECTOR_ATTRIBUTE,
  type A11Y_SELECTORS,
} from './useA11ySelector.constants';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns React props object that can be used to pass to a component's `View` container
 * and/or a key/value pair that can be manually passed to a `View` container's `dataSet` property.
 *
 * Optionally receive extra attributes (e.g. group name and index) to add to the data set.
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useA11ySelector('navbar.left-container');
 *
 *  return (
 *    <View {...a11ySelector.props} />
 *  );
 * }
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useA11ySelector('navbar.left-container');
 *
 *  return (
 *    <View dataSet={{ otherDataAttribute: 'true', ...a11ySelector.props.dataSet }} />
 *  );
 * }
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useA11ySelector('navbar.left-container', { group: 'group-name', index: 1 });
 *
 *  return (
 *    <View {...a11ySelector.props} /> // => data-a11y-selector="navbar.left-container" data-a11y-selector-group="group-name" data-a11y-selector-index="1"
 *  );
 * }
 */
export const useA11ySelector = (
  selector: Selector,
  extraAttributes: ExtraAttributes = {},
) => {
  const { group: groupName, index } = extraAttributes;

  return useMemo(
    () => getA11ySelector(selector, { group: groupName, index }),
    [groupName, index, selector],
  );
};

/**
 * Returns React props object that can be used to pass to a component's `View` container
 * and/or a key/value pair that can be manually passed to a `View` container's `dataSet` property.
 *
 * Optionally receive extra attributes (e.g. group name and index) to add to the data set.
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useMemo(() => getA11ySelector('navbar.left-container'), []);
 *
 *  return (
 *    <View {...a11ySelector.props} />
 *  );
 * }
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useMemo(() => getA11ySelector('navbar.left-container'), []);
 *
 *  return (
 *    <View dataSet={{ otherDataAttribute: 'true', ...a11ySelector.props.dataSet }} />
 *  );
 * }
 *
 * @example
 * const App = () => {
 *  const a11ySelector = useMemo(() => getA11ySelector('navbar.left-container', { group: 'group-name', index: 1 }), []);
 *
 *  return (
 *    <View {...a11ySelector.props} /> // => data-a11y-selector="navbar.left-container" data-a11y-selector-group="group-name" data-a11y-selector-index="1"
 *  );
 * }
 */
export function getA11ySelector(
  selector: Selector,
  extraAttributes: ExtraAttributes = {},
) {
  const { group: groupName, index } = extraAttributes;

  const groupAttribute = `${A11Y_SELECTOR_ATTRIBUTE}-group`;
  const indexAttribute = `${A11Y_SELECTOR_ATTRIBUTE}-index`;

  return {
    props: {
      dataSet: {
        [A11Y_SELECTOR_ATTRIBUTE]: selector,
        ...(groupName ? { [groupAttribute]: groupName } : {}),
        ...(typeof index === 'number' ? { [indexAttribute]: `${index}` } : {}),
      },
    },
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type Selector = ExtractStringValues<typeof A11Y_SELECTORS>;

type ExtraAttributes = {
  group?: string;
  index?: number;
};

type ExtractStringValues<T> = T extends string
  ? T
  : T extends Record<string, any>
    ? { [K in keyof T]: ExtractStringValues<T[K]> }[keyof T]
    : never;
